import { type NextPageContext } from 'next'
import authSDKFactory from '@dlpco/conta-stone-sdk'
import { Mutex } from 'async-mutex'
import { redirectToLoadUser } from 'modules/shared/page-enhancers/src/utils/webview-runtime'
import { destroyCookie } from 'nookies'
import { clientSideOnly } from 'shared/client-side-only'
import {
  APP_VERSION,
  BANKING_API_ENDPOINT,
  BANKING_API_ENDPOINT_V2,
  DEPLOY_TYPE,
  TIME_TO_EXPIRE_TOKEN
} from 'shared/envs'
import { isPlaygroundMode, webviewLocationAssign } from 'shared/page-enhancers'
import { getCookieDomain, isomorphicCookies } from 'shared/storage'

import { Bucket } from '../../lib/bucket'
import { clearPaymentProfileSelection } from '../../lib/payment-profile-storage'
import { coreEvents } from '../analitica'

import {
  type AuthFlow,
  type AuthFlowClientParams,
  type ParseToken,
  type PayloadVersion,
  type RequestRefreshTokenPayload,
  type RequestRefreshTokenResponse
} from './authenticator.types'

const authSDK = authSDKFactory(BANKING_API_ENDPOINT())
const authSDK_V2 = authSDKFactory(BANKING_API_ENDPOINT_V2())
const mutex = new Mutex()

const HUB_COOKIES = ['stn_token_hub', 'stn_refreshToken_hub', 'stn_user_hub', 'stn_payment_profile_hub']

const optionsCookies = { path: '/', maxAge: TIME_TO_EXPIRE_TOKEN }

function getDomainOptions() {
  if (typeof window === 'undefined') return {}
  return { domain: getCookieDomain(window.location.href) }
}

const authFlowClientParams: AuthFlowClientParams = {
  revalidateSession: {
    V1: {},
    V2: {
      client_id: 'web@stone.com.br',
      scope:
        'entity:* expend:* paymentaccount:* card:* pix:* creditline:* credit:borrower:* receivable:* investmentbucket:* acquireraffiliation:* store:* salary:portability principal:consent offline_access profile'
    }
  },
  login: {
    V1: {
      client_id: 'web@stone.com.br'
    },
    V2: {
      client_id: 'web@stone.com.br',
      scope:
        'entity:* expend:* paymentaccount:* card:* pix:* creditline:* credit:borrower:* receivable:* investmentbucket:* acquireraffiliation:* store:* salary:portability principal:consent offline_access profile'
    }
  },
  resetPassword: {
    V1: {},
    V2: {
      client_id: 'web@stone.com.br',
      scope:
        'entity:* expend:* paymentaccount:* card:* pix:* creditline:* credit:borrower:* receivable:* investmentbucket:* acquireraffiliation:* store:* salary:portability principal:consent offline_access profile'
    }
  }
}

async function getValidTokens(
  ctx?: NextPageContext,
  params?: Partial<{
    header?: {
      deviceGeneratedId?: string
      platformId?: string
    }
  }>
): Promise<RequestRefreshTokenResponse> {
  return await mutex.runExclusive(async () => {
    const cookies = isomorphicCookies.getAll(ctx)
    if (isSessionValid(ctx) && cookies.testSandbox !== 'testSandbox' && cookies.token) {
      return { token: cookies.token, refreshToken: cookies.refreshToken }
    }
    const newTokens = await revalidateSession(ctx, params)
    return newTokens
  })
}

async function revalidateSession(
  ctx?: NextPageContext,
  params?: Partial<{
    header?: {
      deviceGeneratedId?: string
      platformId?: string
    }
  }>
) {
  const { token, refreshToken, isWebView } = isomorphicCookies.getAll(ctx)
  const webviewQueryParam = ctx?.query?.webview
  const shouldIgnoreSessionRevalidation = (webviewQueryParam === 'true' || isWebView === 'true') && !isPlaygroundMode()
  if (shouldIgnoreSessionRevalidation) {
    if (isSessionValid(ctx)) {
      return { token, refreshToken }
    }
    try {
      redirectToLoadUser()
    } catch (err) {
      console.error('Erro ao chamar redirectToLoadUser:', err)
    }
    return { token, refreshToken }
  }

  if (isPlaygroundMode() && !isSessionValid(ctx)) {
    console.info(
      '[Authenticator] Playground: token expirado — em webview real, obteria novos tokens do app nativo via app://loadUser'
    )
  }
  if (!refreshToken) {
    coreEvents.events.authenticator.errorRefreshToken()
    throw new Error('No refreshToken available to renew session')
  }

  try {
    const refreshTokenPayload: RequestRefreshTokenPayload = {
      token: token || '',
      refreshToken,
      version: 'V2',
      ...authFlowClientParams.revalidateSession.V2,
      ...params
    }

    const newTokens = (await authSDK_V2.services.requestRefreshToken(
      refreshTokenPayload
    )) as RequestRefreshTokenResponse

    if (!newTokens?.token) {
      throw new Error('Token came back empty from requestRefreshToken')
    }

    const domainOptions = getDomainOptions()

    isomorphicCookies.set('token', newTokens.token, { ctx, ...optionsCookies })
    isomorphicCookies.set('refreshToken', newTokens.refreshToken, { ctx, ...optionsCookies })
    isomorphicCookies.set('stn_token_hub', newTokens.token, {
      ctx,
      ...domainOptions,
      ...optionsCookies
    })
    isomorphicCookies.set('stn_refreshToken_hub', newTokens.refreshToken, {
      ctx,
      ...domainOptions,
      ...optionsCookies
    })

    coreEvents.events.authenticator.tokenRefreshed()

    return newTokens
  } catch (err) {
    console.error('Erro ao revalidar sessão:', JSON.stringify(err))
    coreEvents.events.authenticator.errorRevalidateSession(err)
    throw err
  }
}

function isSessionValid(ctx?: NextPageContext) {
  const { token } = isomorphicCookies.getAll(ctx)
  if (!token) return false

  return expiration() - nowInSeconds() > 60

  function expiration() {
    return parseToken({ token }).exp ?? 0
  }
  function nowInSeconds() {
    return new Date().getTime() / 1000
  }
}

async function encryptWithPublickey<T = unknown>(payload: T): Promise<string> {
  const publicKey = await authSDK_V2.services.fetchPublicKey({ version: 'V2' })
  return authSDK_V2.encrypt({ publicKey, payload })
}

export type LoginProps = {
  username: string
  password: string
  mfaChallengeId?: string
  captcha?: string
  device_token?: string
  headers?: {
    [key: string]: string
  }
}

async function login(
  { username, password, mfaChallengeId, captcha, device_token, headers, ...rest }: LoginProps,
  SDK = authSDK,
  version: PayloadVersion = 'V1'
): Promise<void> {
  const isV2 = version === 'V2'
  const SDK_version = isV2 ? authSDK_V2 : SDK

  let platformId = clientSideOnly(() => Bucket.local.get('platform::id'))
  let deviceGeneratedId = clientSideOnly(() => Bucket.local.get('device::generated::id'))

  if (!platformId) platformId = await SDK_version.deviceIds.requestPlatformId()
  if (!deviceGeneratedId) deviceGeneratedId = await SDK_version.deviceIds.requestDeviceId()

  clientSideOnly(() => Bucket.local.set('platform::id', String(platformId)))
  clientSideOnly(() => Bucket.local.set('device::generated::id', String(deviceGeneratedId)))

  const payload = {
    ...rest,
    username,
    password,
    mfa_challenge_id: mfaChallengeId,
    captcha,
    device_token,
    ...authFlowClientParams['login'][version]
  }
  const { token, refreshToken } = await SDK_version.services.requestLogin({
    payload,
    headers,
    platformId,
    deviceGeneratedId,
    version
  })

  const identityIdCPF = authenticator.parseToken({ token })['stone_subject_id']

  const domainOptions = getDomainOptions()

  isomorphicCookies.set('identityIdCPF', identityIdCPF!)
  isomorphicCookies.set('token', token, optionsCookies)
  isomorphicCookies.set('refreshToken', refreshToken, optionsCookies)
  isomorphicCookies.set('stn_token_hub', token, {
    ...optionsCookies,
    ...domainOptions
  })
  isomorphicCookies.set('stn_refreshToken_hub', refreshToken, {
    ...optionsCookies,
    ...domainOptions
  })
}

async function logout({ redirectUrl }: { redirectUrl: string }): Promise<void> {
  const { token, refreshToken } = isomorphicCookies.getAll()
  isomorphicCookies.batchRemove(['token', 'refreshToken', 'identityId', 'accountId', 'idAssessment'])

  const { domain } = getDomainOptions()

  if (domain) {
    HUB_COOKIES.forEach(cookie => destroyCookie(null, cookie, { domain, path: '/' }))
  }

  clearPaymentProfileSelection()

  // We check APP_VERSION to redirect on PR Preview
  const redirect = DEPLOY_TYPE() === 'preview' ? `/${APP_VERSION()}/${redirectUrl}` : redirectUrl
  try {
    await authSDK_V2.services.requestLogout(token, refreshToken, redirect, 'V2')
  } catch (err) {
    clientSideOnly(() => webviewLocationAssign(redirect))
  }
}

async function resetPassword(
  {
    username,
    captcha,
    headers,
    client_id
  }: {
    username: string
    captcha?: string
    headers?: {
      [key: string]: string
    }
    client_id?: string
  },
  SDK = authSDK,
  version: string = 'V1'
): Promise<void> {
  return SDK.services.resetPassword({
    username,
    captcha,
    headers,
    client_id,
    version
  })
}

async function updatePassword(
  {
    token,
    newPassword
  }: {
    token: string
    newPassword: string
  },
  SDK = authSDK,
  version = 'V1'
): Promise<void> {
  if (!token) {
    throw new Error('Cannot update password without a valid token')
  }
  return SDK.services.updatePassword(token, newPassword, version)
}

function parseToken({ token }: Pick<RequestRefreshTokenResponse, 'token'>): Partial<ParseToken> {
  try {
    const encodedChunk = token.split('.')[1]
    const decodedString = Buffer.from(encodedChunk, 'base64').toString('binary')
    const parsedJson = JSON.parse(decodedString)

    return parsedJson
  } catch (err) {
    return {}
  }
}
export function getAuthenticatorVersion(versionOverride?: 'V1' | 'V2') {
  const authSDK = authSDKFactory(BANKING_API_ENDPOINT())
  const authSDK_V2 = authSDKFactory(BANKING_API_ENDPOINT_V2())
  const { loginVersion } = isomorphicCookies.getAll?.() || {}
  const version = versionOverride || (loginVersion === 'V2' ? 'V2' : 'V1')

  const SDK = version === 'V2' ? authSDK_V2 : authSDK

  return { version, SDK }
}

export function getAuthParams(authFlow: AuthFlow) {
  const deviceGeneratedId = clientSideOnly(() => Bucket.local.get('device::generated::id'))
  const platformId = clientSideOnly(() => Bucket.local.get('platform::id'))

  const { version, SDK } = getAuthenticatorVersion()

  const { client_id = undefined, scope = undefined } = authFlowClientParams[authFlow][version as PayloadVersion] as {
    client_id?: string
    scope?: string
  }

  return {
    SDK,
    version,
    deviceGeneratedId,
    platformId,
    client_id,
    scope
  }
}

export const authenticator = {
  encryptWithPublickey,
  getValidTokens,
  login,
  logout,
  parseToken,
  resetPassword,
  updatePassword,
  isSessionValid,
  revalidateSession,
  getAuthenticatorVersion
}
